import { Spinner } from "@wayfarer/components";
import {
  createContext,
  PropsWithChildren,
  useMemo,
  useContext,
  FC,
} from "react";
import { SaasPortalService } from "@ascend/ui-core/services";
import { useDiscoveryService } from "@ascend/ui-core/hooks";
import { Service } from "@ascend/ui-core/types";
import { useSaasPortalService } from "../hooks/useSaasPortalService";
import { config } from "../config";

const ApplicationContext = createContext<ApplicationContextType | undefined>(
  undefined,
);

type ApplicationContextType = {
  service: Service | undefined;
  saasPortalService: SaasPortalService | undefined;
};

export const ApplicationContextProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  // Use discovery service to get service information
  const { service } = useDiscoveryService(config.appName);
  const { saasPortalService, error: errorMessage } = useSaasPortalService();
  const contextValue = useMemo(() => {
    return {
      saasPortalService,
      service,
    };
  }, [saasPortalService, service]);

  if (errorMessage) {
    return <div>{errorMessage}</div>;
  }

  return (
    <ApplicationContext.Provider value={contextValue}>
      {!saasPortalService ? (
        <Spinner active data-testid="content-spinner" />
      ) : (
        children
      )}
    </ApplicationContext.Provider>
  );
};

export const useApplicationContext = () => {
  const context = useContext(ApplicationContext);
  if (context === undefined) {
    throw new Error(
      "useApplicationContext must be used within an ApplicationContextProvider",
    );
  }
  return context;
};
